import React, { useState, useEffect, useRef } from 'react';
import { Button, Col, Row, Image, Container, Card } from 'react-bootstrap';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import moment from "moment";
import { FaArrowRight } from "react-icons/fa";
import LOGO from '../../assets/images/logo.svg'; // Assuming standard logo for top left
import LOCK_ICON from "../../assets/images/OTP.svg";
import CommonFormikComponent from '../../components/common/CommonFormikComponent';
import { default as CustomInputText } from '../../components/common/CustomInputText';
import Loader from '../../components/common/loader';
import { redirectionRoutes } from '../../routes/redirectionRoutes';
import { jwtDecode } from "jwt-decode";
import { CODE_2FA_DIGIT_ONLY_MSG } from "../../constants/messages";
import { OTP_REQUEST_STATUS } from "../../constants";
import { updateOtpRequestStatus, saveRequestedOTP } from '../../services/site.service';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import GenericModal from "../../components/common/GenericModal";

// Icon URL for the lock (you can replace this with your local asset)
//const LOCK_ICON = "https://cdn-icons-png.flaticon.com/512/3064/3064155.png";

const OTPSchema = Yup.object().shape({
    otp: Yup.string()
        .required('OTP is required')
        .matches(/^[0-9]+$/, CODE_2FA_DIGIT_ONLY_MSG)
});

export default function ScanOTPRequest() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [showOTPInput, setShowOTPInput] = useState(false);
    const [tokenId, setTokenId] = useState<any>(null);
    const [siteDetails, setSiteDetails] = useState<any>(null);
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const [otp, setOTP] = useState<any>(null);
    const [searchParams] = useSearchParams();
    const hasChecked = useRef(false);

    useEffect(() => {
        const tokenParam = searchParams.get('token');
        if (tokenParam) {
            setTokenId(tokenParam);
            try {
                const decoded: any = jwtDecode(tokenParam);
                if (isTokenExpire(decoded)) {
                    if (!hasChecked.current) {
                        toast.error("Token has been expired.");
                        hasChecked.current = true;
                    }
                    navigate("/");
                } else {
                    const details = (decoded as any).data as any;
                    setSiteDetails(details);
                }
            } catch (e) {
                if (!hasChecked.current) {
                    toast.error("Invalid token");
                    hasChecked.current = true;
                }
                navigate("/");
            }
        } else {
            navigate(redirectionRoutes.login.path);
        }
    }, [navigate, searchParams]);

    const isTokenExpire = (decoded: any) => {
        const expirationTime = decoded.exp * 1000;
        return (Date.now() >= expirationTime);
    }

    const handleInitialResponse = (response: 'yes' | 'no') => {
        const payload = {
            token: tokenId,
            userStatus: OTP_REQUEST_STATUS[response]
        }
        setLoading(true);
        updateOtpRequestStatus(payload).then((resp: any) => {
            setLoading(false);
            if (response === 'yes') {
                setShowOTPInput(true);
            } else {
                setIsSubmitted(true);
            }
        })
        .catch((error: any) => {
            setLoading(false);
            toast(error?.data?.message || "Invalid request", { type: toast.TYPE.ERROR });
        })
    };

    const handleOTPSubmit = async (values: any) => {
        setOTP(values.otp);
        setShowConfirmModal(true);
    };

    const handleCloseModal = () => {
        setOTP(null);
        setShowConfirmModal(false);
    }

    const handleSaveOTP = async () => {
        const payload = { token: tokenId, otp: otp }
        setShowConfirmModal(false);
        setLoading(true);
        saveRequestedOTP(payload).then(() => {
            setOTP(null);
            setLoading(false);
            setIsSubmitted(true);
        })
        .catch((error: any) => {
            setLoading(false);
            setOTP(null);
            toast(error?.data?.message || "Invalid request", { type: toast.TYPE.ERROR });
        })
    }

    return (
        <div style={{ backgroundColor: '#f0f4f8', minHeight: '100vh', fontFamily: 'sans-serif' }}>
            <Loader isLoading={loading} />
            
            {/* Logo Section */}
            <div className="p-4">
                <Image src={LOGO} alt="infiltra.ai" height={30} />
            </div>

            <Container className="d-flex flex-column align-items-center justify-content-center" style={{ marginTop: '50px' }}>
                <Card className="border-0 shadow-sm p-5 text-center" style={{ maxWidth: '650px', width: '100%', borderRadius: '24px' }}>
                    
                    {/* Header Icon & Title */}
                    {!isSubmitted ? (
                        <div className="mb-4">
                            <div className="mx-auto mb-3 d-flex align-items-center justify-content-center lock-icon">
                                <Image src={LOCK_ICON} width={70} />
                            </div>
                            <h2 className="fs-4 fw-bold mb-0" style={{ color: '#2d3748' }}>2FA Request</h2>
                        </div>
                    ) : (
                        <div className="mb-4 text-center">
                             <div className="mx-auto mb-3 d-flex align-items-center justify-content-center" style={{ backgroundColor: '#00b894', width: '80px', height: '80px', borderRadius: '50%' }}>
                                <span style={{ color: 'white', fontSize: '40px' }}>✓</span>
                            </div>
                            <h2 className="fs-4 fw-bold mb-1" style={{ color: '#00b894' }}>Submission Completed</h2>
                            <p className="text-muted">Thank you for your response.</p>
                        </div>
                    )}

                    {/* Metadata Grey Bar */}
                    <div className="bg-body-secondary p-3 rounded-4 mb-5 d-flex justify-content-between text-start" style={{ backgroundColor: '#f8f9fa !important' }}>
                        <div>
                            <small className="text-muted d-block">Project</small>
                            <span className="fw-bold">{siteDetails?.projectName || '---'}</span>
                        </div>
                        <div>
                            <small className="text-muted d-block">Site</small>
                            <span className="fw-bold">{siteDetails?.webAppName || '---'}</span>
                        </div>
                        <div>
                            <small className="text-muted d-block">Created</small>
                            <span className="fw-bold">{siteDetails?.createdDate ? moment(siteDetails.createdDate).format("DD MMM YYYY") : '---'}</span>
                        </div>
                    </div>

                    {!isSubmitted && (
                        <div>
                            {!showOTPInput ? (
                                <>
                                    <p className="mb-5 text-muted fs-1-1rem">Your scan has reached a login step that requires two-factor authentication. Are you ready to receive and enter the code now?</p>
                                    <div className="d-flex justify-content-center gap-3">
                                        <Button 
                                            variant="light"
                                            className="px-5 py-2 border fw-semibold rounded-3"
                                            style={{ minWidth: '120px' }}
                                            onClick={() => handleInitialResponse('no')}
                                        >
                                            No
                                        </Button>
                                        <Button 
                                            className="px-5 py-2 border-0 fw-semibold rounded-3"
                                            style={{ backgroundColor: '#ff7675', minWidth: '120px' }}
                                            onClick={() => handleInitialResponse('yes')}
                                        >
                                            Yes
                                        </Button>
                                    </div>
                                </>
                            ) : (
                                <CommonFormikComponent
                                    validationSchema={OTPSchema}
                                    initialValues={{ otp: '' }}
                                    onSubmit={handleOTPSubmit}
                                >
                                    {(formikProps) => (
                                        <div className="text-start">
                                            <p className="text-muted mb-4">Please do not navigate away from this screen. Ensure that you have your 2FA device or email access ready to enter the code below.</p>
                                            <CustomInputText
                                                wrapperClassname="mb-4"
                                                controlId="otp"
                                                placeholder="Enter the OTP Code"
                                                touched={formikProps.touched.otp}
                                                handleBlur={formikProps.handleBlur}
                                                handleChange={formikProps.handleChange}
                                                errorsField={formikProps.errors.otp}
                                                value={formikProps.values.otp}
                                            />
                                            <div className="text-end">
                                                <Button 
                                                    className="px-5 py-2 border-0 fw-semibold rounded-3"
                                                    style={{ backgroundColor: '#ff7675' }}
                                                    type="submit" 
                                                    disabled={!formikProps.isValid || !formikProps.dirty || loading}
                                                >
                                                    {loading ? 'Submitting...' : 'Submit'}
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </CommonFormikComponent>
                            )}
                        </div>
                    )}
                </Card>

                {/* Footer Link */}
                <div className="mt-4">
                    <Link to={"/login"} className="text-decoration-none d-flex align-items-center fw-bold">
                        <FaArrowRight size={14} className="me-2" /> Go to Login
                    </Link>
                </div>
            </Container>

            <GenericModal
                show={showConfirmModal}
                handleClose={handleCloseModal}
                handleAction={handleSaveOTP}
                genericModalHeaderTitle="OTP Submission"
                genericModalBodyContent={`Are you sure you want to submit the OTP ${otp}?`}
                buttonName="Confirm"
            />
        </div>
    );
}